import React from "react"
import { Link as MuiLink } from "@mui/material";
import { Link, Navigate } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { Box, Button, Container, Grid, Typography, Card, Stack, CssBaseline, Divider } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { motion } from "framer-motion"
import {
  ArrowRight,
  CheckCircle2,
  Users,
  TrendingUp,
  Zap,
  BarChart,
  ShieldCheck,
  Star,
  MessageCircle,
  Sparkles,
  Briefcase,
  BookOpen,
  Router,
} from "lucide-react"

import globalLink3 from "../Assets/giiconect2.png"
import globalLink from "../Assets/giiconnect.png"
import globalLink2 from "../Assets/home4.png"
import AskGiiaLogo from "../Assets/askgiia3.png"
import globalLink4 from "../Assets/giiconnect-3.png"
import askgiia from "../Assets/askgiia_word-2.png"




const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#0A2E8A" },
    secondary: { main: "#10682A" },
    background: { default: "#FAFBFC", paper: "#FFFFFF" },
    text: {
      primary: "#0F172A",
      secondary: "#64748B",
    },
  },
  typography: {
    fontFamily: "'Inter', '-apple-system', 'BlinkMacSystemFont', sans-serif",
    h4: {
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h5: {
      fontWeight: 700,
    },
    body1: {
      lineHeight: 1.6,
    },
    body2: {
      lineHeight: 1.6,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: "12px",
          fontSize: "1rem",
          boxShadow: "0 4px 12px rgba(0, 174, 239, 0.25)",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(0, 174, 239, 0.35)",
            transform: "translateY(-2px)",
          },
        },
        outlined: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: "12px",
          fontSize: "1rem",
          transition: "all 0.3s ease",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          border: "1px solid rgba(2, 8, 23, 0.06)",
          transition: "all 0.3s ease",
          "&:hover": {
            borderColor: "rgba(0, 174, 239, 0.2)",
            boxShadow: "0 12px 32px rgba(0, 174, 239, 0.1)",
          },
        },
      },
    },
  },
})

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
}

const fadeInUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] },
  },
}

const scaleInVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, ease: "easeOut" },
  },
}

function Home() {
  const router = useNavigate()

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* NAVBAR */}
      <motion.div initial={{ y: -100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }}>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            width: "100%",
            bgcolor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid rgba(2, 8, 23, 0.06)",
            zIndex: 1000,
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: 72,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  background: "linear-gradient(135deg, #00AEEF 0%, #0A2E8A 100%)",
                  color: "white",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                {/* Hero Image */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <img
                    src={globalLink2}
                    alt="hero illustration"
                    style={{
                      width: "70px",
                      // height: "auto",
                      // objectFit: "contain",
                    }}
                  />
                </motion.div>
              </Box>
              <Typography
                fontWeight="700"
                sx={{
                  fontSize: "16px",
                  background: "linear-gradient(135deg, #0A2E8A 0%, #10682A 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >

              </Typography>
            </Stack>
            <Stack direction="row" spacing={3} alignItems="center">
              <Button
                color="inherit"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  "&:hover": { color: "primary.main" },
                }}
                onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
              >
                Our ecosystem
              </Button>
              {/* <Button
                color="inherit"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  "&:hover": { color: "primary.main" },
                }}
                onClick={() => document.getElementById("testimonials")?.scrollIntoView({ behavior: "smooth" })}
              >
                Testimonials
              </Button> */}
              <Button
                variant="contained"
                onClick={() => router("/signup")}
                sx={{
                  background: "linear-gradient(135deg, #0A2E8A 0%, #0090D9 100%)",
                  borderRadius: "10px",
                  px: 3,
                  py: 1,
                }}
              >
                Get Started
              </Button>
            </Stack>
          </Container>
        </Box>
      </motion.div>

      {/* HERO SECTION */}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pt: 16,
          pb: 8,
          background: "#FFFFFF",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background elements */}
        <motion.div
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 6, repeat: Number.POSITIVE_INFINITY }}
          style={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: "min(300px, 60vw)",
            height: "min(300px, 60vw)",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(0, 174, 239, 0.08), transparent)",
            filter: "blur(40px)",
          }}
        />
        <motion.div
          animate={{ y: [0, 20, 0] }}
          transition={{ duration: 8, repeat: Number.POSITIVE_INFINITY }}
          style={{
            position: "absolute",
            bottom: "10%",
            left: "5%",
            width: "min(300px, 60vw)",
            height: "min(300px, 60vw)",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(245, 126, 71, 0.08), transparent)",
            filter: "blur(40px)",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div variants={containerVariants} initial="hidden" animate="visible" style={{ textAlign: "center" }}>
            {/* Badge */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{ textAlign: "center" }}
            >
              {/* Logo Badge */}
              {/* <motion.div variants={itemVariants}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 3,
                    py: 1.5,
                    // bgcolor: "rgba(0, 174, 239, 0.08)",
                    // borderRadius: "24px",
                    mb: 3,
                    // border: "1px solid rgba(0, 174, 239, 0.2)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <img
                    src={globalLink2}
                    alt="GiiConnect Logo"
                    style={{
                      width: "400px",
                      height: "200px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </motion.div> */}
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              style={{ marginBottom: "20px", margin: "20px", display: "flex", justifyContent: "center" }}
            >
              <img
                src={globalLink3}
                alt="hero illustration"
                style={{
                  width: "280px",
                  height: "150px",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </motion.div>



            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {/* First Line */}
                <Typography
                  sx={{
                    fontSize: { xs: "12px", sm: "20px", md: "32px" },
                    letterSpacing: { xs: "1px", md: "3px" },
                    fontWeight: 450,
                    textTransform: "uppercase",
                    color: "#0A2E8A",
                    lineHeight: 1.4,
                  }}
                >
                  Connecting People. Building Capability.
                </Typography>

                {/* Divider */}
                <Box
                  sx={{
                    width: { xs: "70px", md: "90px" },
                    height: "3px",
                    backgroundColor: "#10682A",
                    borderRadius: "10px",
                  }}
                />

                {/* Second Line */}
                <Typography
                  sx={{
                    fontSize: { xs: "18px", sm: "22px", md: "28px" },
                    letterSpacing: { xs: "4px", md: "8px" },
                    fontWeight: 500,
                    textTransform: "uppercase",
                    color: "#10682A",
                    lineHeight: 1.3,
                  }}
                >
                  Bridging The Digital Divide.
                </Typography>
              </Box>
            </motion.div>

            {/* Subtitle */}
            <motion.div variants={itemVariants}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "18px",
                  color: "text.secondary",
                  mb: 4,
                  maxWidth: "520px",
                  mx: "auto",
                  lineHeight: 1.7,
                  paddingTop: "30px",
                }}
              >
                giiconnect is an AI-powered digital learning, mentorship, and workforce development platform helping individuals build technology skills, access economic opportunities,
                and participate in the global digital economy through accessible, low-bandwidth solutions.
              </Typography>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" sx={{ mb: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => router("/signup")}
                  sx={{
                    color: "#fff",
                    background: "linear-gradient(135deg, #0A2E8A 0%, #0A2E8A 100%)",
                    px: 5,
                    py: 1.8,
                  }}
                  endIcon={<ArrowRight size={20} />}
                >
                  Start Your Journey
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => router("/login")}
                  sx={{
                    borderColor: "rgba(0, 174, 239, 0.3)",
                    color: "text.primary",
                    px: 5,
                    py: 1.8,
                  }}
                >
                  Sign In
                </Button>
              </Stack>
            </motion.div>

            {/* Stats Row */}

          </motion.div>


        </Container>
      </Box>

      {/* ECOSYSTEM SECTION */}
      <Box id="features" sx={{ py: 12, bgcolor: "background.default" }}>
        <Container maxWidth="lg">
          <motion.div
            variants={fadeInUpVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            style={{ textAlign: "center", marginBottom: "64px" }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "32px", md: "48px" },
                color: "primary.main",
                mb: 1,
              }}
            >
              Our ecosystem
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "20px", md: "28px" },
                color: "text.primary",
                mb: 1,
              }}
            >
              Talent is everywhere. Opportunity should be too.
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "16px" }}>
              Five layers, working as one.
            </Typography>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <Box
              sx={{
                position: "relative",
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gridTemplateAreas: {
                  xs: `"hub" "ask" "learn" "mentor" "comm" "opp"`,
                  md: `". ask ." "opp hub learn" "comm . mentor"`,
                },
                gap: 10,
                alignItems: "center",
              }}
            >
              {/* Connector lines (desktop only) — all run to the hub center; the hub's circle occludes the excess so each line appears to terminate exactly at its edge */}
              <Box
                component="svg"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                sx={{
                  display: { xs: "none", md: "block" },
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  zIndex: 0,
                  pointerEvents: "none",
                }}
              >
                <line x1="50" y1="12" x2="50" y2="50" stroke="rgba(15,23,42,0.12)" strokeWidth="0.4" />
                <line x1="28" y1="40" x2="50" y2="50" stroke="rgba(15,23,42,0.12)" strokeWidth="0.4" />
                <line x1="50" y1="50" x2="72" y2="40" stroke="rgba(15,23,42,0.12)" strokeWidth="0.4" />
                <line x1="50" y1="35" x2="35" y2="70" stroke="rgba(15,23,42,0.12)" strokeWidth="0.4" />
                <line x1="50" y1="35" x2="65" y2="70" stroke="rgba(15,23,42,0.12)" strokeWidth="0.4" />
              </Box>

              {/* 01 - Ask Giia */}
              <motion.div
                variants={itemVariants}
                style={{
                  gridArea: "ask",
                  position: "relative",
                  zIndex: 1,
                  marginBottom: "24px",
                }}
              >
                <Card sx={{ p: 3, height: "50%", bgcolor: "#E6F0FF", border: "none", borderRadius: "18px" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2.5 }}>
                    <Box
                      component="img"
                      src={askgiia}
                      alt="Ask Giia"
                      sx={{ height: { xs: 30, md: 36 }, width: "auto", objectFit: "contain" }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 700,
                        fontSize: "22px",
                        color: "#C7CEDE",
                      }}
                    >
                      01
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: 1.5,
                      color: "#5B6478",
                    }}
                  >
                    Your AI guide for career<br />
                    decisions, learning, and<br />
                    opportunity prep, anytime,<br />
                    in your language.
                  </Typography>
                </Card>
              </motion.div>

              {/* 05 - Opportunities */}
              <motion.div variants={itemVariants} style={{ gridArea: "opp", position: "relative", zIndex: 1 }}>
                <Card sx={{ p: 3, height: "50%", bgcolor: "#E6F0FF", border: "none", borderRadius: "18px", transform: { md: "translateY(-150px)" } }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ color: "#0A1A7A" }}>
                      <Briefcase size={32} />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 700,
                        fontSize: "22px",
                        color: "#C7CEDE",
                      }}
                    >
                      05
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 700,
                      fontSize: "22px",
                      color: "#0A1A7A",
                      mb: 1.5,
                    }}
                  >
                    Opportunities
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: 1.5,
                      color: "#5B6478",
                    }}
                  >
                    Discover jobs,<br />
                    scholarships, fellowships,<br />
                    and grants matched to<br />
                    your goals.
                  </Typography>
                </Card>
              </motion.div>

              {/* 02 - Learning pathways */}
              <motion.div variants={itemVariants} style={{ gridArea: "learn", position: "relative", zIndex: 1 }}>
                <Card sx={{ p: 3, height: "50%", bgcolor: "#F5F7FA", border: "none", borderRadius: "18px", transform: { md: "translateY(-150px)" } }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ color: "#106B3A" }}>
                      <BookOpen size={32} />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 700,
                        fontSize: "22px",
                        color: "#C7CEDE",
                      }}
                    >
                      02
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 700,
                      fontSize: "22px",
                      color: "#106B3A",
                      mb: 1.5,
                    }}
                  >
                    Learning pathways
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: 1.5,
                      color: "#5B6478",
                    }}
                  >
                    Build real skills with<br />
                    mobile-friendly lessons<br />
                    you can download and use<br />
                    offline.
                  </Typography>
                </Card>
              </motion.div>

              {/* Hub */}
              <motion.div
                variants={scaleInVariants}
                style={{ gridArea: "hub", display: "flex", justifyContent: "center", position: "relative", zIndex: 1 }}
              >
                <Box
                  sx={{
                    width: { xs: "320px", md: "320px" },
                    height: { xs: "320px", md: "320px" },
                    borderRadius: "50%",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* ① Spinning gradient ring */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: "50%",
                      background:
                        "conic-gradient(from 0deg, #0A2E8A 0%, #10682A 45%, #0A2E8A 55%, #10682A 100%)",
                      "@keyframes hubSpin": {
                        from: { transform: "rotate(0deg)" },
                        to: { transform: "rotate(360deg)" },
                      },
                      animation: "hubSpin 6s linear infinite",
                    }}
                  />

                  {/* ② White inner disc — creates the ring width (adjust inset for thicker/thinner) */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: "13px",
                      borderRadius: "50%",
                      bgcolor: "background.default",
                      zIndex: 1,
                    }}
                  />

                  {/* ③ Static content */}
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      px: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={globalLink3}
                      alt="giiconnect"
                      sx={{
                        width: { xs: "180px", md: "210px" },
                        height: "auto",
                        objectFit: "contain",
                        display: "block",
                        mb: 1.5,
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 400,
                        fontSize: "16px",
                        color: "#5B6478",
                      }}
                    >
                      five layers, one ecosystem
                    </Typography>
                  </Box>
                </Box>
              </motion.div>


              {/* 04 - Community */}
              <motion.div variants={itemVariants} style={{ gridArea: "comm", position: "relative", zIndex: 1 }}>
                <Card
                  sx={{
                    p: 3,
                    height: "50%",
                    bgcolor: "#F5F7FA",
                    border: "none",
                    borderRadius: "18px",
                    transform: { md: "translate(115px, -45px)" },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ color: "#106B3A" }}>
                      <MessageCircle size={32} />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 700,
                        fontSize: "22px",
                        color: "#C7CEDE",
                      }}
                    >
                      04
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 700,
                      fontSize: "22px",
                      color: "#106B3A",
                      mb: 1.5,
                    }}
                  >
                    Community
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: 1.5,
                      color: "#5B6478",
                    }}
                  >
                    Learn alongside others in<br />
                    a supportive, moderated<br />
                    space where every<br />
                    question is welcome.
                  </Typography>
                </Card>
              </motion.div>

              {/* 03 - Mentorship */}
              <motion.div variants={itemVariants} style={{ gridArea: "mentor", position: "relative", zIndex: 1 }}>
                <Card
                  sx={{
                    p: 3,
                    height: "50%",
                    bgcolor: "#E6F0FF",
                    border: "none",
                    borderRadius: "18px",
                    transform: { md: "translate(-115px, -45px)" },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ color: "#0A1A7A" }}>
                      <Users size={32} />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 700,
                        fontSize: "22px",
                        color: "#C7CEDE",
                      }}
                    >
                      03
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 700,
                      fontSize: "22px",
                      color: "#0A1A7A",
                      mb: 1.5,
                    }}
                  >
                    Mentorship
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: 1.5,
                      color: "#5B6478",
                    }}
                  >
                    Connect with real<br />
                    professionals who give<br />
                    their time to guide your<br />
                    career or business.
                  </Typography>
                </Card>
              </motion.div>
            </Box>
          </motion.div>

          <Box sx={{ textAlign: "center", mt: 6 }}>
            <Button
              onClick={() => router("/signup")}
              variant="contained"
              size="large"
              endIcon={<ArrowRight size={20} />}
              sx={{
                bgcolor: "#0A2E8A",
                color: "white",
                px: 4,
                py: 1.5,
                "&:hover": { bgcolor: "#08246e" },
              }}
            >
              See how it all connects
            </Button>
          </Box>
        </Container>
      </Box>

      {/* TESTIMONIALS SECTION */}


      {/* CTA SECTION */}
      <Box
        sx={{
          py: 12,
          background: "linear-gradient(135deg, #0A2E8A 0%, #0A2E8A 100%)",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Number.POSITIVE_INFINITY }}
          style={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
            filter: "blur(40px)",
          }}
        />
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 6, repeat: Number.POSITIVE_INFINITY }}
          style={{
            position: "absolute",
            bottom: "-50px",
            left: "-50px",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
            filter: "blur(40px)",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div variants={fadeInUpVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <Typography
              variant="h4"
              sx={{
                fontSize: "40px",
                fontWeight: 700,
                mb: 2,
                color: "white",
              }}
            >
              Ready to Start Your Journey?
            </Typography>
            <Typography
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "18px",
                mb: 4,
              }}
            >
              Join thousands of innovators already shaping the future.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => router("/signup")}
              sx={{
                bgcolor: "white",
                color: "primary.main",
                fontWeight: 700,
                px: 6,
                py: 1.8,
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.95)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              Get Started Today
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* FOOTER */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderTop: "1px solid rgba(2, 8, 23, 0.06)",
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} sx={{ mb: 6 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  ml: 1,
                }}
              >
                <img
                  src={globalLink}
                  alt="GiiConnect Logo"
                  style={{
                    margin: "10px",
                    width: "170px",
                    height: "75px",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Connecting People. Building Capability. Bridging The Digital Divide.
              </Typography>
            </Grid>
            {[
              {
                title: "Product",
                links: ["Features", "Pricing", "Community"],
              },
              {
                title: "Company",
                links: ["About", "Blog", "Careers"],
              },
              {
                title: "Legal",
                links: ["Privacy", "Terms", "Contact"],
              },
            ].map((col, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Typography fontWeight="700" sx={{ mb: 2, color: "text.primary" }}>
                  {col.title}
                </Typography>
                <Stack spacing={1}>
                  {col.links.map((link, j) => (
                    <Typography
                      key={j}
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        cursor: "pointer",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {link}
                    </Typography>
                  ))}
                </Stack>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: { xs: "center", md: "left" } }}>
              © 2026 giiconnect. All rights reserved.
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ color: "#979797", fontSize: 13 }}>
                Made by giiconnect Team
              </Typography>
            </Box>
            <Stack direction="row" spacing={3}>
              {[
                { name: "X", url: "https://x.com/giiconnect" },
                { name: "LinkedIn", url: "https://www.linkedin.com/company/giiconnect/" },
                { name: "Instagram", url: "https://www.instagram.com/giiconnect/" },
              ].map((social) => (
                <MuiLink
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      cursor: "pointer",
                      fontWeight: 600,
                      "&:hover": { color: "primary.main" },
                    }}
                  >
                    {social.name}
                  </Typography>
                </MuiLink>
              ))}
            </Stack>
          </Stack>
        </Container>
        <Divider sx={{ my: 3 }} />
      </Box>
    </ThemeProvider>
  )
}

export default Home